/**
 * @file The action component allows users to duplicate a shift or shift template to the same or the other
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { FormControl } from '@angular/forms';
import { OverlayProvider } from '../../ui/overlay-provider';
import { OverlayService } from '../../core/overlay.service';
import { AlertsService } from '../../core/alerts.service';
import { BreezeService } from '../../core/breeze.service';
import { DomainService } from '../../domain.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { AbstractBase } from '../../core/helpers/abstract-base';
import { mergeMap } from 'rxjs/operators';
var BatchPlanmacroComponent = /** @class */ (function (_super) {
    tslib_1.__extends(BatchPlanmacroComponent, _super);
    /**
     * Creates an instance of BatchPlanmacroComponent.
     * @param {OverlayProvider} overlayProvider Contains the source shift (template)
     * @param {OverlayService} overlayService Can close the overlay
     * @param {AlertsService} alertsService Can show alerts
     * @param {DomainService} domainService Can save changes
     */
    function BatchPlanmacroComponent(overlayProvider, overlayService, alertsService, domainService, translateService) {
        var _this = _super.call(this) || this;
        _this.overlayService = overlayService;
        _this.alertsService = alertsService;
        _this.domainService = domainService;
        _this.translateService = translateService;
        /**
         * The controls are necessary for the form to work
         */
        _this.timeControl = new FormControl();
        _this.entityControl = new FormControl();
        /** Indicates when the batch start */
        _this.runAt = moment()
            .add(5, 'minutes')
            .toDate();
        _this.provider = overlayProvider;
        return _this;
    }
    /**
     * Sets the planmacro to be displayed
     */
    BatchPlanmacroComponent.prototype.ngOnInit = function () {
        var providerEntity = this.provider.entity;
        if (!providerEntity) {
            this.alertsService.createAlert('warning', 'APP.TEMPLATES.PLANMACRO_NOT_FOUND');
            return;
        }
        this.entity = providerEntity;
        if (!this.entity) {
            this.alertsService.createAlert('warning', 'APP.TEMPLATES.PLANMACRO_EXPECTED');
        }
    };
    /**
     * Schedules the planmacro
     * @param {any} event The button that is pressed
     */
    BatchPlanmacroComponent.prototype.schedule = function (event) {
        var _this = this;
        if (event) {
            event.target.classList.add('sym-working');
        }
        var m = this.runAt; // moment(this.runAt).toDate();
        var seconds = m.getHours() * 3600 + m.getMinutes() * 60 + m.getSeconds();
        var ISOseconds = moment.duration(seconds, 'seconds').toISOString();
        this.entity['RunAt'] = ISOseconds;
        this.domainService.saveChanges(function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.startRunscript()];
                    case 1:
                        _a.sent();
                        this.cancel();
                        return [2 /*return*/];
                }
            });
        }); }).toPromise();
    };
    /**
     * Closes the overlay without saving
     */
    BatchPlanmacroComponent.prototype.cancel = function () {
        this.overlayService.closeOverlay();
    };
    /**
    * Starts the startRunscript script
    */
    BatchPlanmacroComponent.prototype.startRunscript = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var error_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 5, , 6]);
                        return [4 /*yield*/, this.startReplaceSubjectWith()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.startMoveTo()];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.startScriptInit()];
                    case 3:
                        _a.sent();
                        return [4 /*yield*/, this.startScriptRun()];
                    case 4:
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        error_1 = _a.sent();
                        this.alertsService.createAlert('warning', 'APP.DOMAIN.SCHEDULE_PLANMACRO_UNSUCCESSFULLY', error_1);
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Starts the startInvokeMethod script
     * @param {string} method The shift template entity
     * @param {any} [parameters=[]] Parameters passed on to the method.
     * @returns {Promise<any>}
     */
    BatchPlanmacroComponent.prototype.startInvokeMethod = function (method, parameters) {
        if (parameters === void 0) { parameters = []; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        _this.subscriptionPool.add(BreezeService.invokeMethod(_this.entity, method, parameters)
                            .pipe(mergeMap(function (value) {
                            return _this.domainService.saveChanges(function (error) {
                                if (error) {
                                    reject();
                                }
                            });
                        }))
                            .subscribe(function (response) {
                            var lastResult = response.entities.find(function (foundEntity) {
                                return foundEntity['Origin'] === _this.entity.Oid && foundEntity['entityType'] == null;
                            });
                            if (!lastResult || !lastResult['Value']) {
                                reject();
                            }
                            resolve(lastResult);
                        }));
                    })];
            });
        });
    };
    /**
     * Starts the startReplaceSubjectWith script
     * @returns {Promise<any>}
     */
    BatchPlanmacroComponent.prototype.startReplaceSubjectWith = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var subjectStartDate, subjectOid, lastResult;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        subjectStartDate = this.subject['Start'];
                        subjectOid = this.subject['Oid'];
                        return [4 /*yield*/, this.startInvokeMethod('ReplaceSubjectsWith', [subjectOid])];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                lastResult['Name'] === 'ReplaceSubjectsWith' && moment.isDate(subjectStartDate) ? resolve() : reject();
                            })];
                }
            });
        });
    };
    /**
    * Starts the startMoveTo script
    * @returns {Promise<any>}
    */
    BatchPlanmacroComponent.prototype.startMoveTo = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var subjectStartDate, lastResult;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        subjectStartDate = new Date(this.subject['Start']);
                        subjectStartDate.setHours(0, 0, 0, 0);
                        return [4 /*yield*/, this.startInvokeMethod('MoveTo', [subjectStartDate])];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                lastResult['Name'] === 'MoveTo' ? resolve() : reject();
                            })];
                }
            });
        });
    };
    /**
    * Starts the startScriptInit script
    * @returns {Promise<any>}
    */
    BatchPlanmacroComponent.prototype.startScriptInit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var lastResult;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.startInvokeMethod('ScriptInit', [])];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                lastResult['Name'] === 'ScriptInit' ? resolve() : reject();
                            })];
                }
            });
        });
    };
    /**
    * Starts the startScriptRun script
    * @returns {Promise<any>}
    */
    BatchPlanmacroComponent.prototype.startScriptRun = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var lastResult;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.startInvokeMethod('ScriptRun', [0])];
                    case 1:
                        lastResult = _a.sent();
                        return [2 /*return*/, new Promise(function (resolve, reject) {
                                if (lastResult['Value'] !== 'SCHEDULED') {
                                    reject('ScriptRun had an unexpected result: ' + lastResult['Value']);
                                }
                                _this.alertsService.createAlert('success', 'APP.DOMAIN.SCHEDULE_PLANMACRO_SUCCESSFULLY');
                                lastResult['Name'] === 'ScriptRun' ? resolve() : reject();
                            })];
                }
            });
        });
    };
    Object.defineProperty(BatchPlanmacroComponent.prototype, "planmacroFormat", {
        get: function () {
            return {
                dataSource: 'ShiftItems',
                entity: 'Shift',
                fields: ['Name', 'Start', 'Finish', 'Number'],
                displayFormat: '[Name] [Number] ([Start] | [Finish])'
            };
        },
        enumerable: true,
        configurable: true
    });
    return BatchPlanmacroComponent;
}(AbstractBase));
export { BatchPlanmacroComponent };
